import React from 'react';
import QRCodeGenerator from './QRCodeGenerator';

/**
 * FBR Digital Invoicing System Logo Component
 *
 * This component displays the official FBR Digital Invoicing System logo
 * as required by FBR specifications (Page 34 of the technical document)
 *
 * Uses the actual FBR logo image as specified in the documentation:
 * "Use FBR Digital Invoicing System image to print on receipts"
 */
const FBRLogo = ({
  size = 'medium',
  className = '',
  showText = true,
  variant = 'full' // 'full', 'icon', 'text'
}) => {
  const sizeClasses = {
    small: 'w-12 h-12',
    medium: 'w-16 h-16',
    large: 'w-24 h-24',
    xlarge: 'w-32 h-32'
  };

  const textSizeClasses = {
    small: 'text-xs',
    medium: 'text-sm',
    large: 'text-base',
    xlarge: 'text-lg'
  };

  // Official FBR Digital Invoicing System Logo Image
  const FBRLogoImage = () => (
    <div className="fbr-logo-wrapper relative">
      <img
        src="/fbrlogo.png"
        alt="FBR Digital Invoicing System"
        className={`${sizeClasses[size]} object-contain ${className}`}
        title="Federal Board of Revenue - Digital Invoicing System"
        onError={(e) => {
          if (!e.currentTarget.dataset.fallbackAttempted) {
            e.currentTarget.dataset.fallbackAttempted = 'true';
            e.currentTarget.src = '/fbrlogo1.svg';
            return;
          }
          e.currentTarget.style.display = 'none';
          const fallback = e.currentTarget.nextElementSibling;
          if (fallback) fallback.style.display = 'flex';
        }}
      />
      {/* Fallback text if image fails to load */}
      <div
        className={`${sizeClasses[size]} bg-blue-600 text-white rounded-lg flex flex-col items-center justify-center text-xs font-bold hidden`}
        style={{ display: 'none' }}
      >
        <div>FBR</div>
        <div className="text-[8px]">DIGITAL</div>
        <div className="text-[8px]">INVOICING</div>
        <div className="text-[8px]">SYSTEM</div>
      </div>
    </div>
  );

  // Text-only version
  const FBRText = () => (
    <div className={`bg-blue-600 text-white px-3 py-2 rounded ${textSizeClasses[size]} font-bold text-center ${className}`}>
      <div>DIGITAL</div>
      <div>INVOICING</div>
      <div>SYSTEM</div>
    </div>
  );

  // Icon-only version (uses the official FBR logo image)
  const FBRIcon = () => (
    <div className={`fbr-icon-wrapper ${className}`}>
      <FBRLogoImage />
    </div>
  );

  if (variant === 'icon') {
    return <FBRIcon />;
  }

  if (variant === 'text') {
    return <FBRText />;
  }

  // Full version with logo and optional text
  return (
    <div className={`fbr-logo-container flex items-center space-x-2 ${className}`}>
      <FBRLogoImage />
      {showText && (
        <div className={`text-blue-600 ${textSizeClasses[size]} font-bold`}>
          <div>DIGITAL</div>
          <div>INVOICING</div>
          <div>SYSTEM</div>
        </div>
      )}
    </div>
  );
};

/**
 * FBR Compliance Badge
 * Shows FBR compliance status with optional FBR invoice number
 */
export const FBRComplianceBadge = ({
  status = 'compliant',
  className = '',
  size = 'medium',
  fbrInvoiceNumber = null,
  showFbrNumber = false
}) => {
  const statusColors = {
    compliant: 'bg-green-100 text-green-800 border-green-200',
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    error: 'bg-red-100 text-red-800 border-red-200'
  };

  const statusText = {
    compliant: 'FBR Compliant',
    pending: 'FBR Pending',
    error: 'FBR Error'
  };

  const sizeClasses = {
    small: 'text-xs px-2 py-1',
    medium: 'text-sm px-3 py-1',
    large: 'text-base px-4 py-2'
  };

  return (
    <div className={`inline-flex flex-col items-end space-y-1 ${className}`}>
      <div className={`inline-flex items-center space-x-1 border rounded-full ${statusColors[status]} ${sizeClasses[size]}`}>
        <FBRLogo size="small" variant="icon" className="w-4 h-4" showText={false} />
        <span className="font-medium">{statusText[status]}</span>
      </div>

      {/* Show FBR Invoice Number directly under badge if available and requested */}
      {showFbrNumber && fbrInvoiceNumber && (
        <div className="text-right">
          <p className="text-xs text-gray-500">FBR #</p>
          <p className="text-xs font-mono font-bold text-blue-600">{fbrInvoiceNumber}</p>
        </div>
      )}
    </div>
  );
};

/**
 * FBR Invoice Header
 * Complete header with FBR logo and compliance information
 */
export const FBRInvoiceHeader = ({
  invoiceNumber,
  fbrInvoiceNumber,
  status = 'compliant',
  className = '',
  invoiceId = null,
  invoiceData = null,
  showQRCode = false
}) => {
  return (
    <div className={`fbr-invoice-header border-b border-gray-200 pb-4 mb-4 ${className}`}>
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-4">
          <FBRLogo size="large" />
          <div>
            <h3 className="text-lg font-bold text-gray-900">Digital Invoice</h3>
            <p className="text-sm text-gray-600">Federal Board of Revenue</p>
            <p className="text-sm text-gray-600">Government of Pakistan</p>
          </div>
        </div>
        
        <div className="flex items-start space-x-4">
          <div className="text-right">
            {/* Enhanced FBR Compliance Badge with FBR number */}
            <FBRComplianceBadge
              status={status}
              fbrInvoiceNumber={fbrInvoiceNumber}
              showFbrNumber={true}
            />

            {/* Additional FBR Invoice Number display for emphasis when available */}
            {fbrInvoiceNumber && (
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                <p className="text-xs text-blue-600 font-medium">FBR Invoice Number</p>
                <p className="text-sm font-mono font-bold text-blue-800">{fbrInvoiceNumber}</p>
              </div>
            )}

            {/* Regular Invoice Number */}
            {invoiceNumber && (
              <div className="mt-2">
                <p className="text-xs text-gray-500">Invoice Number</p>
                <p className="text-sm font-mono">{invoiceNumber}</p>
              </div>
            )}

            {/* Scenario Number */}
            {(invoiceData?.scenario_id || invoiceData?.scenarioId) && (
              <div className="mt-2">
                <p className="text-xs text-gray-500">Generated using Scenario</p>
                <p className="text-sm font-mono font-semibold text-green-700">{invoiceData?.scenario_id || invoiceData?.scenarioId}</p>
              </div>
            )}

            {/* Pending message when no FBR number */}
            {!fbrInvoiceNumber && status === 'pending' && (
              <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                <p className="text-xs text-orange-600">FBR submission pending</p>
              </div>
            )}
          </div>

          {/* QR Code in the status area */}
          {showQRCode && invoiceId && invoiceData && (
            <div className="ml-4 text-center">
              <QRCodeGenerator
                invoiceId={invoiceId}
                invoiceData={invoiceData}
                size={80}
                type="invoice"
                minimal={true}
              />
              {/* FBR Invoice Number under QR Code */}
              {fbrInvoiceNumber && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500">FBR Invoice Number</p>
                  <p className="text-xs font-mono font-bold text-blue-800 break-all">{fbrInvoiceNumber}</p>
                </div>
              )}
              {/* Scenario Number under QR Code */}
              {(invoiceData?.scenario_id || invoiceData?.scenarioId) && (
                <div className="mt-1">
                  <p className="text-xs text-gray-500">Scenario</p>
                  <p className="text-xs font-mono font-semibold text-green-700">{invoiceData?.scenario_id || invoiceData?.scenarioId}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FBRLogo;
